import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { translate as translateHOC } from 'react-admin';
import { withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import OpenInNew from '@material-ui/icons/OpenInNew';
import GetApp from '@material-ui/icons/GetApp';
import { keys } from './constants';

// Fetches the refund note as a blob via loadPdf (an <iframe src> can't carry the bearer
// token) and renders it via a blob URL, revoked on close.
const styles = ({ spacing: { unit }, palette }) => ({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: `${unit}px`,
    padding: `${unit}px ${unit * 2}px`,
    borderBottom: `1px solid ${palette.divider}`,
  },
  spacer: {
    flexGrow: 1,
  },
  content: {
    height: '72vh',
    padding: 0,
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: `${unit * 2}px`,
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
  },
  btnIcon: {
    marginRight: unit / 2,
    fontSize: 18,
  },
});

const RefundNoteViewerDialog = ({ classes, translate, open, loadPdf, onClose }) => {
  const [url, setUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error

  useEffect(() => {
    if (!open || !loadPdf) return undefined;
    let objectUrl;
    let cancelled = false;

    const load = async () => {
      setStatus('loading');
      try {
        const data = await loadPdf();
        const pdfBlob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
        objectUrl = window.URL.createObjectURL(pdfBlob);
        if (cancelled) {
          // Viewer closed mid-fetch: revoke the just-created URL (the cleanup already ran and
          // saw an undefined objectUrl, so it can't revoke this one).
          window.URL.revokeObjectURL(objectUrl);
        } else {
          setUrl(objectUrl);
          setStatus('ready');
        }
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      setUrl(null);
      setStatus('idle');
    };
  }, [open, loadPdf]);

  const isReady = status === 'ready';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className={classes.bar}>
        <Typography variant="subheading">{translate(keys.refundNoteTitle)}</Typography>
        <div className={classes.spacer} />
        <Button
          size="small"
          component="a"
          href={isReady ? url : undefined}
          target="_blank"
          rel="noopener"
          disabled={!isReady}
        >
          <OpenInNew className={classes.btnIcon} />
          {translate(keys.openInTab)}
        </Button>
        <Button
          size="small"
          variant="outlined"
          component="a"
          href={isReady ? url : undefined}
          download="invoiceRefundNote.pdf"
          disabled={!isReady}
        >
          <GetApp className={classes.btnIcon} />
          {translate(keys.download)}
        </Button>
        <IconButton onClick={onClose} aria-label={translate(keys.close)}>
          <Close />
        </IconButton>
      </div>
      <DialogContent className={classes.content}>
        {status === 'loading' && (
          <div className={classes.centered}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              {translate(keys.loadingPdf)}
            </Typography>
          </div>
        )}
        {status === 'error' && (
          <div className={classes.centered}>
            <Typography color="error" align="center">
              {translate(keys.pdfError)}
            </Typography>
          </div>
        )}
        {isReady && url && <iframe src={url} title={translate(keys.refundNoteTitle)} className={classes.iframe} />}
      </DialogContent>
    </Dialog>
  );
};

RefundNoteViewerDialog.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  open: PropTypes.bool,
  loadPdf: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export const RefundNoteViewerDialogTest = RefundNoteViewerDialog;

export default compose(
  withStyles(styles),
  translateHOC
)(RefundNoteViewerDialog);
